// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import copy from 'copy-to-clipboard';
import { filter, get, some } from 'lodash';
import { oneOfType, func, bool, object, string, number, arrayOf } from 'prop-types';
import {
  FormLabel,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Button,
  Checkbox,
  Typography,
  Grid,
  withStyles,
  Dialog,
  Tooltip,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import {
  createKey,
  updateKey,
  deleteKey,
  fetchKeyNameUnique,
  fetchSingleApp,
  fetchKeyStoresByApiKey,
  fetchOneOAuthClient,
  createOAuthClient,
  updateOAuthClient,
  deleteOAuthClient,
  resetOneOAuthClient,
} from '../../../../actions/application';
import {
  getCreateKeyStatus,
  getUpdateKeyStatus,
  getDeleteKeyStatus,
  getIsUniqueKey,
  getCreatedKey,
  getDeleteKeyErrors,
  getSingleOAuthClient,
  getCreateOAuthClientStatus,
  getUpdateOAuthClientStatus,
  getDeleteOAuthClientStatus,
  getOAuthClientErrors,
  getCreatedOAuthClient,
} from '../../../../reducers/application';

import {
  fetchListDetails,
} from '../../../../actions/requests';

import {
  fetchClientDefinitions,
} from '../../../../actions/authProviders';

import {
  getClientDefintionsList,
} from '../../../../reducers/authProviders';

import {
  FormTextField,
  FormButtonGroup,
  AlertDialog,
  AlertMessages,
  LoadingDialog,
  FormSelect,
} from '../../../../components';
import styles from './styles';
import { hasError } from '../../../../utils';
import { getI18n, getI18nFormattedMessage } from '../../../../utils/intl';
import { processErrors } from '../utils';
import {
  ALERT_SUCCESS,
  ALERT_ERROR,
  API_KEY_STATUS_DELETE_FAILED,
  API_KEY_STATUS_DISABLED,
  API_KEY_STATUS_ENABLED,
  API_KEY_STATUS_EXPIRED,
  APPLICATION_STATUS_DELETE_FAILED,
  APPLICATION_STATUS_DELETE_PENDING_APPROVAL,
  APPLICATION_STATUS_DISABLED,
  APPLICATION_STATUS_PENDING_APPROVAL,
  APPLICATION_STATUS_REJECTED,
  AUTH_METHOD_CERTIFICATE,
  AUTH_METHOD_NONE,
  AUTH_METHOD_SECRET,
  DEFAULT_PORTAL_AUTH_PROVIDER_UUID,
  API_KEYS_APPLICATION,
  RESET,
  SUCCESS,
  FAIL,
  AUTH_PROVIDER_TYPE_PORTAL_DCR,
  AUTH_PROVIDER_TYPE_EXTERNAL,
  API_KEY_STATUS_PENDING_REGISTRATION,
  APPLICATION_STATUS_INCOMPLETE,
} from '../../../../constants';
import { Close, ReportProblem } from '@material-ui/icons';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { hasCRUDPermissions } from '../../../../utils';
import RegisterClientInfoDialog from '../Configuration/RegisterClientInfoDialog';

const SECRET_TYPE_HASHED = 'HASHED_SECRET';
const SECRET_TYPE_PLAIN = 'PLAIN_SECRET';

const keyStatusOptions = [
  {
    key: API_KEY_STATUS_ENABLED,
    label: getI18nFormattedMessage('label.application.key.state.enabled'),
  },
  {
    key: API_KEY_STATUS_DISABLED,
    label: getI18nFormattedMessage('label.application.key.state.disabled'),
  },
];

const statusLabelMap = {
  [API_KEY_STATUS_DELETE_FAILED]: getI18nFormattedMessage('label.application.key.state.delete.failed'),
  [API_KEY_STATUS_DISABLED]: getI18nFormattedMessage('label.application.key.state.disabled'),
  [API_KEY_STATUS_ENABLED]: getI18nFormattedMessage('label.application.key.state.enabled'),
  [API_KEY_STATUS_EXPIRED]: getI18nFormattedMessage('label.application.key.state.expired'),
  [API_KEY_STATUS_PENDING_REGISTRATION]: getI18nFormattedMessage('label.application.key.state.pending.registration'),
};

export const deleteKeyDisabledStates = [
  APPLICATION_STATUS_DELETE_FAILED,
  APPLICATION_STATUS_DELETE_PENDING_APPROVAL,
  APPLICATION_STATUS_DISABLED,
  APPLICATION_STATUS_PENDING_APPROVAL,
  APPLICATION_STATUS_REJECTED,
];

export const shouldDisableDeleteKeyBtn = (applicationStatus, isDefaultKey) =>
  deleteKeyDisabledStates.includes(applicationStatus) || isDefaultKey;

export const isStatusFieldDisabled =
  ({ defaultKey, keyIsDefault }) => (defaultKey || keyIsDefault);

export const isDefaultKeyFieldDisabled =
  ({ editable, defaultKey, status, keyStatus, keyCount }) => (
    some([
      !editable,
      defaultKey,
      status === API_KEY_STATUS_DISABLED, // Status persisted is Disabled
      status === API_KEY_STATUS_EXPIRED, // Status persisted is EXPIRED
      keyStatus === API_KEY_STATUS_DISABLED, // Status on the form is Disabled
      keyCount === 0,
    ])
  );

const MIN_TEXT_AREA_HEIGHT = 10;
const MAX_TEXT_AREA_HEIGHT = 30;
export const EditKey = (props) => {
  const {
    applicationDetails = {},
    idx,
    editable = true,
    applicationUuid,
    authProviders,
    details: {
      name = null,
      status = '',
      defaultKey = false,
      oauthCallbackUrl,
      oauthScope,
      oauthType,
      apiKey = '',
      authProviderUuid = '',
      keySecret = '',
      keySecretHashed = false,
      isRegenerateSecret = false,
      authMethod,
      clientRegistrationType,
    },
    isUniqueKey,
    notifyMessages,
    appSecretHashingMetadata,
    closeAction,
    classes,
    deleteKeyStatus = '',
    updateKeyStatus = '',
    createKeyStatus = '',
    createdKey,
    keyCount,
    isLocking,
    deleteKeyErrors,
    fetchSingleApp,
    isPortalAdmin,
    certificateCount,
    user,
    fetchListDetails,
    clientDefinitions,
    oAuthClient = {},
    createOAuthClientStatus,
    updateOAuthClientStatus,
    deleteOAuthClientStatus,
    oAuthClientErrors,
    createdOAuthClient,
    resetOneOAuthClient,
  } = props;
  const DEFAULT_JSON = '{}';
  const intl = getI18n(useIntl());
  const [keyName, setKeyName] = useState(name);
  const [keyStatus, setKeyStatus] = useState(status);
  const [keyIsDefault, setKeyIsDefault] = useState(defaultKey);
  const [keyOAuthCallbackUrl, setKeyOAuthCallbackUrl] = useState(oauthCallbackUrl);
  const [keyOAuthScope, setKeyOAuthScope] = useState(oauthScope);
  const [keyOAuthType, setKeyOAuthType] = useState('PUBLIC');
  const [keyClientSecret, setKeyClientSecret] = useState(keySecret);
  const [keyAuthMethod, setKeyAuthMethod] = useState(AUTH_METHOD_SECRET);
  const [keyIsHashed, setKeyIsHashed] =
    useState(keySecretHashed ? SECRET_TYPE_HASHED : SECRET_TYPE_PLAIN);
  const [keyErrors, setKeyErrors] = useState({});
  const [keyRegenerateSecret, setRegenerateSecret] = useState(isRegenerateSecret);
  const [isGenerateSecretDialogOpen, setIsGenerateSecretDialogOpen] = useState(false);
  const [deleteClicked, setDeleteClicked] = useState(false);
  const [keyCreating, setKeyCreating] = useState(false);
  const [keyUpdating, setKeyUpdating] = useState(false);
  const [keyDeleting, setKeyDeleting] = useState(false);
  const [showLoadingDialog, setShowLoadingDialog] = useState(false);
  const [showGenericLoadingDialog, setShowGenericLoadingDialog] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState(localStorage.getItem('notificationMessage'));
  const [notificationStatus, setNotificationStatus] = useState(localStorage.getItem('notificationStatus'));
  const [createdApiKey, setCreatedApiKey] = useState('');
  const [createdSecret, setCreatedSecret] = useState('');
  const [isDisableSave, setIsDisableSave] = useState(true);
  const [selectedAuthProviderUuid, setSelectedAuthProviderUuid] = useState(authProviderUuid
    || DEFAULT_PORTAL_AUTH_PROVIDER_UUID);
  const [selectedAuthProvider, setSelectedAuthProvider] = useState({});
  const [selectedClientDefinitionUuid, setSelectedClientDefinitionUuid] = useState('0');
  const [selectedClientDefinition, setSelectedClientDefinition] = useState({});
  const [clientMetadata, setClientMetadata] = useState(DEFAULT_JSON);
  const [userProvidedAPIKey, setUserProvidedAPIKey] = useState(apiKey);
  const [textAreaHeight, setTextAreaHeight] = useState(MIN_TEXT_AREA_HEIGHT);
  const [invalidJsonMetadata, setInvalidJsonMetadata] = useState(false);
  const [isRegisteredKeyInfoDialogOpen, setIsRegisteredKeyInfoDialogOpen] = useState(false);
  const [isDcrDefferedInfoDialog, setIsDcrDefferedInfoDialog] = useState(false);

  const closeDialog = () => {
    props.onSaveDone();
    closeAction();
  }

  useEffect(() => { setKeyName(name); }, [name]);
  useEffect(() => { setKeyStatus(status); }, [status]);
  useEffect(() => { setKeyIsDefault(defaultKey); }, [defaultKey]);
  useEffect(() => {
    if (keyCount === (apiKey ? 1 : 0)) {
      setKeyIsDefault(true);
    }
  }, [keyCount, apiKey]);
  useEffect(() => { setKeyOAuthCallbackUrl(oauthCallbackUrl); }, [oauthCallbackUrl]);
  useEffect(() => { setKeyOAuthScope(oauthScope); }, [oauthScope]);
  useEffect(() => { oauthType ? setKeyOAuthType(oauthType) : '' }, [oauthType]);
  useEffect(() => { setKeyClientSecret(keySecret); }, [keySecret]);
  useEffect(() => {
    if (keySecretHashed) {
      setKeyIsHashed(SECRET_TYPE_HASHED);
    } else {
      setKeyIsHashed(SECRET_TYPE_PLAIN);
    }
  }, [keySecretHashed]);
  useEffect(() => { setRegenerateSecret(isRegenerateSecret); }, [isRegenerateSecret]);
  useEffect(() => {
    if (authMethod) {
      setKeyAuthMethod(authMethod);
    } else {
      setKeyAuthMethod(AUTH_METHOD_SECRET);
    }
  }, [authMethod, certificateCount]);

  useEffect(() => {
    if (apiKey && clientRegistrationType === AUTH_PROVIDER_TYPE_PORTAL_DCR) {
      props.fetchOneOAuthClient(applicationUuid, apiKey);
      if (status === API_KEY_STATUS_PENDING_REGISTRATION && authProviderUuid) {
        props.fetchClientDefinitions(authProviderUuid);
      }
    }
  }, [apiKey]);

  useEffect(() => {
    if (oAuthClient && apiKey) {
      setClientMetadata(JSON.stringify(oAuthClient.clientMetadata, null, 2));
    }
  }, [oAuthClient]);

  useEffect(() => {
    // return function to clean up redux state as needed
    // for now this function only resets the DCR that was opened
    // so that openining dialog again does not compare old data
    return resetOneOAuthClient;
  }, []);

  const [showKeyStoreErrorAlert, setShowKeyStoreErrorAlert] = useState(false);
  const [showClientMetadataReloadAlert, setShowClientMetadataReloadAlert] = useState(false);
  const [validateJsonTimer, setValidateJsonTimer] = useState({});
  const [reloadClientDefinitionUuid, setReloadClientDefinitionUuid] = useState('');

  useEffect(() => {
    if (keyDeleting) {
      if (deleteKeyStatus === 'SUCCESS') {
        setKeyDeleting(false);
        setShowLoadingDialog(false);
        const confirmMessage = isLocking ? intl.getI18nMessage('label.application.details.keys.key.delete.request.success') :
          intl.getI18nMessage('label.application.details.keys.key.delete.success');
        notifyMessages(confirmMessage, ALERT_SUCCESS, true);
        fetchListDetails({ entityUuid: applicationUuid });
        closeDialog();
      } else if (deleteKeyStatus === 'FAIL') {
        setKeyDeleting(false);
        setDeleteClicked(false);
        setShowLoadingDialog(false);
        const defaultErrorMessage = intl.getI18nFormattedMessage('label.application.request.submit.error');
        const errorMessage = processErrors(deleteKeyErrors, defaultErrorMessage);
        if (deleteKeyErrors.length > 0 && deleteKeyErrors[0].field === 'keyStoreCheck') {
          if (isPortalAdmin) {
            setShowKeyStoreErrorAlert(true);
          }
          notifyMessagesInDialog(errorMessage, ALERT_ERROR, true);
        } else {
          if (deleteKeyErrors.length > 0 && deleteKeyErrors[0].field === 'apiKey') {
            closeDialog();
            notifyMessages(deleteKeyErrors[0].error, ALERT_ERROR, true);
          }
          setShowKeyStoreErrorAlert(false);
          notifyMessagesInDialog(intl.getI18nMessage('label.application.details.keys.key.delete.error'), ALERT_ERROR, true);
        }
      }
      fetchSingleApp(applicationUuid);
    }
  }, [deleteKeyStatus]);

  useEffect(() => {
    if (keyDeleting) {
      if (deleteOAuthClientStatus === SUCCESS) {
        setKeyDeleting(false);
        setShowGenericLoadingDialog(false);
        const confirmMessage = isLocking ? intl.getI18nMessage('label.application.details.keys.oauth.client.delete.request.success') :
          intl.getI18nMessage('label.application.details.keys.oauth.client.delete.success');
        notifyMessages(confirmMessage, ALERT_SUCCESS, true);
        fetchListDetails({ entityUuid: applicationUuid });
        closeDialog();
      } else if (deleteOAuthClientStatus === FAIL) {
        setKeyDeleting(false);
        setDeleteClicked(false);
        setShowGenericLoadingDialog(false);
        const defaultErrorMessage = intl.getI18nFormattedMessage('label.application.details.keys.oauth.client.delete.error');
        const errorMessage = processErrors(oAuthClientErrors, defaultErrorMessage);
        let firstErrorField;
        if (oAuthClientErrors.length > 0) {
          firstErrorField = oAuthClientErrors[0].field;
        }
        if (firstErrorField === 'keyStoreCheck') {
          if (isPortalAdmin) {
            setShowKeyStoreErrorAlert(true);
          } else {
            notifyMessagesInDialog(errorMessage, ALERT_ERROR, false);
          }
        } else {
          if (firstErrorField === 'apiKey') {
            closeDialog();
            notifyMessages(oAuthClientErrors[0].error, ALERT_ERROR, false);
          }
          setShowKeyStoreErrorAlert(false);
          notifyMessagesInDialog(errorMessage || intl.getI18nMessage('label.application.details.keys.oauth.client.delete.error'), ALERT_ERROR, false);
        }
      }
      fetchSingleApp(applicationUuid);
    }
  }, [deleteOAuthClientStatus]);

  useEffect(() => {
    if (keyUpdating) {
      if (updateKeyStatus === 'SUCCESS') {
        notifyMessages(intl.getI18nMessage(`label.application.details.keys.key.update.${isLocking ? 'request.' : ''}success`), ALERT_SUCCESS, true);
        closeDialog();
        props.onSaveDone();
      } else if (updateKeyStatus === 'FAIL') {
        notifyMessagesInDialog(intl.getI18nMessage('label.application.details.keys.key.update.error'), ALERT_ERROR, true);
      }
      if (updateKeyStatus) {
        setKeyUpdating(false);
      }
    }
  }, [updateKeyStatus]);

  useEffect(() => {
    if (keyUpdating) {
      if (updateOAuthClientStatus !== RESET) {
        setKeyUpdating(false);
        setShowGenericLoadingDialog(false);
      }
      if (updateOAuthClientStatus === SUCCESS) {
        setIsDisableSave(true);
        notifyMessages(intl.getI18nMessage(`label.application.details.oauth.client.update.${isLocking ? 'request.' : ''}success`), ALERT_SUCCESS, true);
        closeDialog();
        props.onSaveDone();
      } else if (updateOAuthClientStatus === FAIL) {
        const errorMessage = processErrors(oAuthClientErrors, intl.getI18nMessage('label.application.details.keys.oauth.client.update.error'));
        notifyMessagesInDialog(errorMessage, ALERT_ERROR, false);
      }
    }
  }, [updateOAuthClientStatus]);

  useEffect(() => {
    if (keyCreating && createKeyStatus === 'FAIL') {
      notifyMessagesInDialog(intl.getI18nMessage('label.application.details.keys.key.create.error'), ALERT_ERROR, true);
      setKeyCreating(false);
    }
  }, [createKeyStatus]);

  useEffect(() => {
    if (createOAuthClientStatus !== RESET) {
      setKeyCreating(false);
      setShowGenericLoadingDialog(false);
    }
    if (keyCreating && createOAuthClientStatus === SUCCESS) {
      setIsDisableSave(true);
      props.fetchSingleApp(applicationUuid);
      if (get(createdOAuthClient, 'status') === API_KEY_STATUS_PENDING_REGISTRATION) {
        setIsDcrDefferedInfoDialog(true);
      } else {
        props.onSaveDone();
        props.fetchKeyStoresByApiKey(get(createdOAuthClient, 'clientId'));
        setTimeout(() =>
          props.fetchKeyStoresByApiKey(get(createdOAuthClient, 'clientId')), 10000);
        setIsRegisteredKeyInfoDialogOpen(true);
      }
    } else if (keyCreating && createOAuthClientStatus === FAIL) {
      const errorMessage = processErrors(oAuthClientErrors,
        intl.getI18nMessage('label.application.details.keys.oauth.client.create.failed'));
      notifyMessagesInDialog(errorMessage, ALERT_ERROR, false);
    }
  }, [createOAuthClientStatus]);

  useEffect(() => {
    setKeyErrors({
      ...keyErrors,
      name: (isUniqueKey === false),
    });
  }, [isUniqueKey]);
  useEffect(() => {
    if (createdKey && keyCreating) {
      setShowLoadingDialog(false);
      setCreatedApiKey(createdKey.apiKey);
      setCreatedSecret(createdKey.keySecret);
      if (createdKey.authMethod === AUTH_METHOD_SECRET) {
        setIsGenerateSecretDialogOpen(true);
      } else {
        const message = isLocking ?
          intl.getI18nMessage('label.application.details.keys.key.create.request.success')
          :
          intl.getI18nMessage('label.application.details.keys.key.create.success');
        notifyMessages(message, ALERT_SUCCESS, true);
        closeDialog();
      }
      props.fetchKeyStoresByApiKey(createdKey.apiKey);
      setTimeout(() => {
        props.fetchKeyStoresByApiKey(createdKey.apiKey);
      }, 10000);
      fetchSingleApp(applicationUuid);
    }
  }, [createdKey]);

  const notifyMessagesInDialog = (message, status, timeout) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
    if (timeout) {
      setTimeout(() => {
        notifyMessagesInDialog('', '')
      }, 3000);
    }
  };
  const checkErrors = (obj) => Object.keys(obj).filter(key => obj[key]).length > 0;

  const clearNotifyMessages = () => {
    setNotificationStatus('');
    setNotificationMessage('');
  }

  const expandCollapseTextAreas = () => {
    if (textAreaHeight === MIN_TEXT_AREA_HEIGHT) {
      setTextAreaHeight(MAX_TEXT_AREA_HEIGHT);
    } else {
      setTextAreaHeight(MIN_TEXT_AREA_HEIGHT);
    }
  }

  const createOAuthClient = () => {
    const oAuthClientData = {
      authProviderUuid: selectedAuthProviderUuid,
      name: keyName,
      defaultKey: keyIsDefault,
      clientMetadata: JSON.parse(clientMetadata),
      appUuid: applicationUuid,
    }
    setKeyCreating(true);
    props.createOAuthClient(oAuthClientData);
  }
  const updateOAuthClient = () => {
    setRegenerateSecret(false);
    setKeyUpdating(true);
    props.updateOAuthClient({
      appUuid: applicationUuid,
      clientId: apiKey,
      name: keyName,
      defaultKey: keyIsDefault,
      status: keyStatus,
      clientMetadata: JSON.parse(clientMetadata),
      authProviderUuid: selectedAuthProviderUuid,
    });
  }
  const saveKey = () => {
    clearNotifyMessages();
    let hasErrors = checkErrors(keyErrors);
    let errors = { ...keyErrors };

    if (keyName === '' || keyName === null) {
      errors = { ...errors, name: true };
      hasErrors = true;
    } else if (keyName.length > 255) {
      errors = { ...errors, name: true };
      hasErrors = true;
    }

    if (hasErrors) {
      setKeyErrors(errors);
    } else {
      const keyData = {
        apiKey,
        appUuid: applicationUuid,
        authProviderUuid: selectedAuthProviderUuid,
        name: keyName,
        defaultKey: keyIsDefault,
        authMethod: keyAuthMethod,
        oauthCallbackUrl: keyOAuthCallbackUrl,
        oauthScope: keyOAuthScope,
        oauthType: keyOAuthType,
        keySecretHashed: keyIsHashed === SECRET_TYPE_HASHED,
      };

      if (apiKey) {
        if (clientRegistrationType === AUTH_PROVIDER_TYPE_PORTAL_DCR) {
          updateOAuthClient();
        } else {
          setIsDisableSave(true);
          let data = {
            ...keyData,
            status: keyStatus,
          };

          if (keyRegenerateSecret) {
            data = {
              ...data,
              keySecret: keyClientSecret,
            };
          }
          setRegenerateSecret(false);
          setKeyUpdating(true);
          props.updateKey(data);
        }
      } else {
        if (selectedAuthProvider.clientRegistrationType === AUTH_PROVIDER_TYPE_PORTAL_DCR) {
          setShowGenericLoadingDialog(true);
          createOAuthClient();
        } else {
          setIsDisableSave(true);
          if (selectedAuthProvider.clientRegistrationType === AUTH_PROVIDER_TYPE_EXTERNAL) {
            keyData.apiKey = userProvidedAPIKey;
          }
          const data = {
            ...keyData,
            status: keyStatus,
          };
          setKeyCreating(true);
          props.createKey(data);
        }
      }
    }
  };
  const validateJson = (value) => {
    try {
      JSON.parse(value);
      setInvalidJsonMetadata(false);
    } catch (e) {
      setInvalidJsonMetadata(true);
    }
  };
  const handleChange = async (fieldName, fieldValue) => {
    switch (fieldName) {
      case 'name':
        setKeyName(fieldValue ? fieldValue : null);
        setKeyErrors({ ...keyErrors, name: hasError(fieldValue, true) });
        await props.fetchKeyNameUnique(fieldValue, apiKey, applicationUuid);
        break;
      case 'status':
        setKeyStatus(fieldValue);
        break;
      case 'oauthCallbackUrl':
        setKeyOAuthCallbackUrl(fieldValue ? fieldValue : null);
        break;
      case 'scope':
        setKeyOAuthScope(fieldValue ? fieldValue : null);
        break;
      case 'type':
        setKeyOAuthType(fieldValue);
        break;
      case 'defaultKey':
        setKeyIsDefault(fieldValue);
        break;
      case 'isHashed':
        setKeyIsHashed(fieldValue);
        setKeyErrors({ ...keyErrors, isHashed: hasError(fieldValue, true) });
        break;
      case 'authMethod':
        setKeyAuthMethod(fieldValue);
        if ((fieldValue === AUTH_METHOD_NONE) && (authProviders.length > 0)) {
          setSelectedAuthProviderUuid(authProviders[0].uuid);
          setSelectedAuthProvider(authProviders[0]);
          if (authProviders[0].clientRegistrationType === AUTH_PROVIDER_TYPE_PORTAL_DCR) {
            props.fetchClientDefinitions(authProviders[0].uuid);
          }
        }
        break;
      case 'authProvider':
        setSelectedAuthProviderUuid(fieldValue);
        setSelectedAuthProvider(authProviders
          .find(provider => provider.uuid === fieldValue));
        setSelectedClientDefinitionUuid('0');
        setSelectedClientDefinition({});
        if (fieldValue === DEFAULT_PORTAL_AUTH_PROVIDER_UUID) {
          setKeyAuthMethod(AUTH_METHOD_SECRET);
          setUserProvidedAPIKey('');
        } else {
          props.fetchClientDefinitions(fieldValue);
          setKeyAuthMethod(AUTH_METHOD_NONE);
        }
        break;
      case 'userProvidedAPIKey':
        setUserProvidedAPIKey(fieldValue);
        break;
      case 'clientMetadata':
        clearTimeout(validateJsonTimer);
        setValidateJsonTimer(setTimeout(() => validateJson(fieldValue), 1000));
        setClientMetadata(fieldValue);
        break;
      default:
    }
  };

  const onClientDefintionSelect = (fieldValue) => {
    if (clientMetadata !== DEFAULT_JSON && (!selectedClientDefinitionUuid ||
      JSON.stringify(selectedClientDefinition.clientMetadata, null, 2) !== clientMetadata)) {
      setShowClientMetadataReloadAlert(true);
      setReloadClientDefinitionUuid(fieldValue);
    } else {
      changeClientDefintionSelect(fieldValue);
    }
  }

  const changeClientDefintionSelect = (fieldValue) => {
    setIsDisableSave(false);
    setShowClientMetadataReloadAlert(false);
    setSelectedClientDefinitionUuid(fieldValue);
    const cdSelected = clientDefinitions.find(cd => cd.uuid === fieldValue);
    setSelectedClientDefinition(cdSelected);
    const clientMetadataStr = JSON.stringify(cdSelected.clientMetadata, null, 2);
    setClientMetadata(clientMetadataStr);
    clearTimeout(validateJsonTimer);
    validateJson(clientMetadataStr);
  }
  const onReloadClientMetadata = () => {
    setShowClientMetadataReloadAlert(true);
    setReloadClientDefinitionUuid(selectedClientDefinitionUuid)
  }

  useEffect(() => {
    const enableSave = props.details['name'] !== keyName || props.details['status'] !== keyStatus ||
      props.details['defaultKey'] !== keyIsDefault ||
      (props.details['authMethod'] === AUTH_METHOD_SECRET &&
        (props.details['oauthCallbackUrl'] !== keyOAuthCallbackUrl ||
          props.details['oauthScope'] !== keyOAuthScope || props.details['oauthType'] !== keyOAuthType)) ||
      (selectedAuthProvider.clientRegistrationType === AUTH_PROVIDER_TYPE_EXTERNAL &&
        userProvidedAPIKey) ||
      (props.details['clientRegistrationType'] === AUTH_PROVIDER_TYPE_PORTAL_DCR &&
        getMetadataToCompare(oAuthClient['clientMetadata']) !== clientMetadata);
    setIsDisableSave(!enableSave);
  }, [keyName, keyStatus, keyIsDefault, keyOAuthCallbackUrl, keyOAuthScope,
    keyOAuthType, userProvidedAPIKey, selectedAuthProviderUuid, clientMetadata]);

  const displayHashingOptions = get(appSecretHashingMetadata, 'plaintextAllowed');
  const secretOptions = [
    {
      uuid: SECRET_TYPE_HASHED,
      name: 'Hashed',
    },
    {
      uuid: SECRET_TYPE_PLAIN,
      name: 'Plaintext',
    },
  ];

  useEffect(() => {
    if (!apiKey) {
      let hashType = SECRET_TYPE_HASHED;
      if (!displayHashingOptions) {
        if (get(appSecretHashingMetadata, 'algorithm') && !get(appSecretHashingMetadata, 'plaintextAllowed')) {
          hashType = SECRET_TYPE_HASHED;
        } else {
          hashType = SECRET_TYPE_PLAIN;
        }
      }
      setKeyIsHashed(hashType);
    }
  }, [appSecretHashingMetadata]);


  const deleteApiKey = () => {
    setKeyDeleting(true);
    if (clientRegistrationType === AUTH_PROVIDER_TYPE_PORTAL_DCR) {
      setShowGenericLoadingDialog(true);
      props.deleteOAuthClient({
        oauthClientDetails: {
          appUuid: applicationUuid,
          clientId: apiKey,
        },
      });
    } else {
      setShowLoadingDialog(true);
      props.deleteKey({
        keyDetails: {
          appUuid: applicationUuid,
          apiKey,
        },
      });
    }
  }

  const deleteApiKeyIgnoringKeyStoreErrors = () => {
    setKeyDeleting(true);
    setShowKeyStoreErrorAlert(false);
    if (clientRegistrationType === AUTH_PROVIDER_TYPE_PORTAL_DCR) {
      setShowGenericLoadingDialog(true);
      props.deleteOAuthClient({
        oauthClientDetails: {
          appUuid: applicationUuid,
          clientId: apiKey,
        },
        ignoreKeyStoreCheck: true,
      });
    } else {
      setShowLoadingDialog(true);
      props.deleteKey({
        keyDetails: {
          appUuid: applicationUuid,
          apiKey,
        },
        ignoreKeyStoreCheck: true,
      });
    }
  };
  const isDefaultPortalAuthProviderSelected =
    selectedAuthProviderUuid === DEFAULT_PORTAL_AUTH_PROVIDER_UUID;
  let authMethods = [
    {
      uuid: AUTH_METHOD_NONE,
      name: getI18nFormattedMessage('label.application.details.apikeys.client.none'),
      description: '',
    },
    {
      uuid: AUTH_METHOD_CERTIFICATE,
      name: getI18nFormattedMessage('label.application.details.apikeys.client.certificate'),
      description: getI18nFormattedMessage('label.application.details.apikeys.client.certificate.description'),
      disabled: !certificateCount || certificateCount < 1,
    },
    {
      uuid: AUTH_METHOD_SECRET,
      name: getI18nFormattedMessage('label.application.details.apikeys.client.secret'),
      description: getI18nFormattedMessage('label.application.details.apikeys.client.secret.description'),
    },
  ];
  if (isDefaultPortalAuthProviderSelected) {
    authMethods = filter(authMethods, item => item.uuid !== AUTH_METHOD_NONE);
  }
  const forceDeleteAPIKey = () => {
    setKeyDeleting(true);
    if (clientRegistrationType === AUTH_PROVIDER_TYPE_PORTAL_DCR) {
      props.deleteOAuthClient({
        oauthClientDetails: {
          appUuid: applicationUuid,
          clientId: apiKey,
        },
        ignoreKeyStoreCheck: true,
        isForceDelete: true,
      });
    } else {
      props.deleteKey({
        keyDetails: {
          appUuid: applicationUuid,
          apiKey,
        },
        ignoreKeyStoreCheck: true,
        isForceDelete: true,
      });
    }
  }

  const showCopied = (value, valueType) => {
    let strType = '';
    if (valueType === 'API_KEY') {
      strType = 'API Key';
    } else if (valueType === 'SECRET') {
      strType = 'Secret';
    }
    notifyMessagesInDialog(`${strType}: ${value} was copied to the clipboard.`, ALERT_SUCCESS, true);
  };
  const keyStoreAlertTitle = intl.getI18nMessage('label.application.details.key.store.alert.title');
  const keyStoreAlertDescription = intl.getI18nMessage('label.application.details.key.store.alert.description');
  const keyStoreAlertOauthClientDescription = intl.getI18nMessage('label.application.details.key.store.alert.oauth.client.description');
  const keyStoreAlertSubmitText = intl.getI18nMessage('label.application.details.key.store.alert.submit');
  const keyStoreAlertCancelText = intl.getI18nMessage('label.cancel.button');
  const deleteText = isLocking ?
    intl.getI18nFormattedMessage('label.application.details.keys.key.generate.secret.dialog.submit.delete.button')
    :
    intl.getI18nFormattedMessage('label.application.details.keys.key.generate.secret.dialog.delete.button');
  const deleteBtnLabel = keyStatus === API_KEY_STATUS_DELETE_FAILED ?
    intl.getI18nFormattedMessage('label.application.details.keys.key.generate.secret.dialog.force.delete.button')
    :
    deleteText;
  const deleteTooltip = keyStatus === API_KEY_STATUS_DELETE_FAILED && isPortalAdmin ?
    intl.getI18nFormattedMessage('label.application.key.force.delete.confirmation.text')
    : "";
  const deleteFn = keyStatus === API_KEY_STATUS_DELETE_FAILED && isPortalAdmin ?
    forceDeleteAPIKey
    :
    deleteApiKey;
  const renderKeyStatus = () => {
    const showStateSelection =
      (keyStatus === API_KEY_STATUS_ENABLED) || (keyStatus === API_KEY_STATUS_DISABLED);

    return apiKey &&
      (<FormControl component="fieldset" className={classes.statusField}>
        <FormLabel variant="h4" component="legend" focused={false}>
          {getI18nFormattedMessage('label.application.details.keys.key.edit.status.title')}
        </FormLabel>
        {showStateSelection ? (<RadioGroup
          className={classes.statusRadioGroup}
          data-apim-test={`${apiKey}-status-radio-group`}
          value={keyStatus}
          onChange={(e) => setKeyStatus(e.target.value)}
        >
          {keyStatusOptions.map((item) => (
            <Fragment key={item.key}>
              <FormControlLabel
                control={
                  <Radio
                    classes={{ root: classes.iconRoot }}
                    disabled={isStatusFieldDisabled({ defaultKey, keyIsDefault }) || !apiKey}
                  />
                }
                key={item.key}
                label={item.label}
                value={item.key}
              />
            </Fragment>
          ))}
        </RadioGroup>) : (
          <span
            data-apim-test={`${apiKey}-status-label`}
            className={classes.statusLabel}
          >
            {statusLabelMap[keyStatus]}
          </span>
        )}
      </FormControl>);
  };

  const createdKeyKeySecretHashed = get(createdKey, 'keySecretHashed');
  const isSecretAuthMethod = keyAuthMethod === AUTH_METHOD_SECRET;
  const canDeleteKey = hasCRUDPermissions(user, ['DELETE'], API_KEYS_APPLICATION);

  const isDeleteKeyDisabled = shouldDisableDeleteKeyBtn(applicationDetails.status, defaultKey);

  const getMetadataToCompare = (metadata) => {
    if (metadata) {
      return JSON.stringify(metadata, null, 2);
    }
    return DEFAULT_JSON;
  }

  return (
    <div id="key-edit-dialog" className={classes.keyEditDialog}>
      {notificationMessage &&
        <AlertMessages
          id="app-details-notification"
          variant={notificationStatus}
          message={notificationMessage}
          containerClass={classes.notificationWidthClass}
          onClose={() => { notifyMessagesInDialog('', ''); }}
        />
      }
      <Dialog
        open={isGenerateSecretDialogOpen}
        onClose={() => setIsGenerateSecretDialogOpen(false)}
        aria-labelledby="generate-secret-dialog"
        id="generate-secret-dialog"
      >
        <div className={classes.generateSecretDialog}>
          <h4>{intl.getI18nFormattedMessage('label.application.details.keys.key.new.key.created.dialog.heading')}</h4>
          <p className={classes.generateSecretDialogContent}>
            {intl.getI18nFormattedMessage('label.application.details.keys.key.new.key.generated.dialog.content')}
          </p>
          <Grid item xs={12} className={classes.rightPanel}>
            <div className={classes.fieldCopyContainer}>
              <FormTextField
                id={`client-key`}
                name={getI18nFormattedMessage('label.application.key')}
                value={createdApiKey}
                disabled
                fieldContainerClass={classes.textField}
              />
              <Button
                id="copy-key-btn"
                variant="outlined"
                onClick={() => {
                  copy(createdApiKey);
                  showCopied(createdApiKey, 'API_KEY', notifyMessages);
                }}
                className={classes.copyButton}
              >
                {getI18nFormattedMessage('label.application.details.keys.overview.copy.button')}
              </Button>
            </div>
            <div className={classes.fieldCopyContainer}>
              <FormTextField
                id="client-secret"
                name={getI18nFormattedMessage('label.application.key.shared.secret')}
                value={createdSecret}
                fieldContainerClass={classes.textField}
                disabled
                errorHelperText={false}
                helperText={''}
              />
              <Button
                id="copy-secret-btn"
                data-apim-test="copy-secret-btn"
                variant="outlined"
                onClick={() => {
                  copy(createdSecret);
                  showCopied(createdSecret, 'SECRET', notifyMessages);
                }}
                className={classes.copyButton}
              >
                {getI18nFormattedMessage('label.application.details.keys.overview.copy.button')}
              </Button>
            </div>
            {createdKeyKeySecretHashed &&
              <div className={classes.secretHelpText}>
                <WarningIcon className={classes.secretInfoWarningIcon} />
                <Typography>
                  {intl.getI18nFormattedMessage('label.application.key.shared.secret.hashed.generated.help')}
                </Typography>
              </div>}
          </Grid>
          <div className={classes.dialogButtonPanel}>
            <Button
              id="key-created-dialog-close"
              data-apim-test="key-created-dialog-close"
              className={classes.saveButton}
              color="secondary"
              variant="contained"
              onClick={() => {
                setIsGenerateSecretDialogOpen(false);
                closeDialog();
              }}
            >
              {intl.getI18nFormattedMessage('label.application.details.keys.key.generate.secret.dialog.close.button')}
            </Button>
          </div>
        </div>
      </Dialog>

      <RegisterClientInfoDialog
        registeredClientId={get(createdOAuthClient, 'clientId')}
        keyClientSecret={get(createdOAuthClient, 'clientSecret')}
        clientMetadata={get(createdOAuthClient, 'clientMetadata')}
        notifyMessages={notifyMessages}
        isRegisteredKeyInfoDialogOpen={isRegisteredKeyInfoDialogOpen}
        setIsRegisteredKeyInfoDialogOpen={setIsRegisteredKeyInfoDialogOpen}
        onRegistrationDialogClose={closeAction}
      />
      <AlertDialog
        isOpen={isDcrDefferedInfoDialog}
        title={intl.getI18nMessage('label.oauth.client.dcr.deferred.title')}
        description={get(applicationDetails, 'status') === APPLICATION_STATUS_INCOMPLETE ?
          intl.getI18nMessage('label.oauth.client.dcr.deferred.incomplete.description') :
          intl.getI18nMessage('label.oauth.client.dcr.deferred.wf.description')}
        cancelText={intl.getI18nMessage('label.oauth.client.dcr.deferred.ok.text')}
        onClose={() => {
          setIsDcrDefferedInfoDialog.bind(null, false);
          closeAction();
        }}
        onCancel={() => {
          setIsDcrDefferedInfoDialog.bind(null, false);
          closeAction();
        }}
        dialogId={'app-dcr-deferred-dialog'}
        showSubmit={false}
      />
      <AlertDialog
        isOpen={showKeyStoreErrorAlert}
        title={keyStoreAlertTitle}
        description={clientRegistrationType === AUTH_PROVIDER_TYPE_PORTAL_DCR ?
          keyStoreAlertOauthClientDescription : keyStoreAlertDescription}
        submitText={keyStoreAlertSubmitText}
        cancelText={keyStoreAlertCancelText}
        onClose={setShowKeyStoreErrorAlert.bind(null, false)}
        onSubmit={deleteApiKeyIgnoringKeyStoreErrors}
        onCancel={setShowKeyStoreErrorAlert.bind(null, false)}
        dialogId={'app-delete-key-store-checks-dialog'}
        submitButtonClass={classes.alertSubmitButton}
      />
      <AlertDialog
        isOpen={showClientMetadataReloadAlert}
        title={intl.getI18nMessage('label.oauth.client.metadata.modified.title')}
        description={intl.getI18nMessage('label.oauth.client.metadata.modified.description')}
        submitText={intl.getI18nMessage('label.oauth.client.metadata.modified.submit.text')}
        cancelText={intl.getI18nMessage('label.cancel.button')}
        onClose={setShowClientMetadataReloadAlert.bind(null, false)}
        onSubmit={() => changeClientDefintionSelect(reloadClientDefinitionUuid)}
        onCancel={setShowClientMetadataReloadAlert.bind(null, false)}
        dialogId={'oauth-client-metadata-replace'}
        submitButtonClass={classes.alertSubmitButton}
      />
      <LoadingDialog
        className={classes.loadingDialog}
        isOpen={showLoadingDialog}
        title={intl.getI18nMessage('label.api.key.delete.loading.title')}
        description={intl.getI18nMessage('label.api.key.delete.loading.text')}
        dialogId="deleting-key-dialog"
      />
      <LoadingDialog
        className={classes.genericLoadingDialog}
        isOpen={showGenericLoadingDialog}
        title={intl.getI18nMessage('label.api.key.generic.loading.title')}
        description={intl.getI18nMessage('label.api.key.generic.loading.text')}
        dialogId="generic-loading-dialog"
      />
      <div className={classes.headingLine}>
        <div className={classes.headingAndDelete}>
          <h3 className={classes.heading}>{intl.getI18nMessage('label.application.details.keys.key.edit.heading')}</h3>
          {apiKey && canDeleteKey && (deleteClicked ?
            <div className={classes.deleteConfirmPanel}>
              <Button
                className={classes.errorBackgroundButton}
                onClick={deleteFn}
              >
                {intl.getI18nFormattedMessage('label.application.details.keys.key.generate.secret.dialog.delete.confirm.button')}
              </Button>
              <Button
                color="primary"
                variant="outlined"
                onClick={() => setDeleteClicked(false)}
                className={classes.cancelButton}
              >
                {intl.getI18nFormattedMessage('label.application.details.keys.key.generate.secret.dialog.cancel.button')}
              </Button>
            </div>
            :
            <Tooltip
              arrow disableFocusListener disableTouchListener placement="bottom"
              title={deleteTooltip}
            >
              <Button
                className={classes.deleteKeyButton}
                color="primary"
                data-apim-test="key-delete-button"
                disabled={isDeleteKeyDisabled}
                variant="outlined"
                onClick={() => setDeleteClicked(true)}
              >
                {deleteBtnLabel}
              </Button>
            </Tooltip>
          )}
        </div>
        <Button
          color="primary"
          variant="outlined"
          className={classes.closeIconButton}
          onClick={() => closeDialog()}
        >
          <Close />
        </Button>
      </div>
      {isLocking &&
        <div className={classes.lockingWarning}>
          <ReportProblem className={classes.warningIcon} />
          <div>{getI18nFormattedMessage('label.application.details.keys.locking.warning')}</div>
        </div>
      }
      <div className={classes.keyDetails}>
        <div className={textAreaHeight === MIN_TEXT_AREA_HEIGHT ? `${classes.showDetails}` : `${classes.hideDetails}`}>
          <FormTextField
            id="key-name"
            name={intl.getI18nMessage('label.application.key.name')}
            value={keyName}
            helperText={intl.getI18nMessage('label.application.key.name.help')}
            handleChange={(value) => { handleChange('name', value); }}
            error={keyErrors.name || (isUniqueKey === false)}
            errorHelperText={keyErrors.name || (isUniqueKey === false)}
            maxLength="255"
            disabled={!editable}
            autoFocus={true}
          />
          <FormControlLabel
            classes={{
              root: classes.defaultCheckboxContainer,
            }}
            control={
              <Checkbox
                classes={{ root: classes.iconRoot }}
                defaultChecked={defaultKey}
                disabled=
                {isDefaultKeyFieldDisabled({ editable, defaultKey, status, keyStatus, keyCount })}
                size="small"
                checked={keyIsDefault}
                onChange={(event) => { handleChange('defaultKey', event.target.checked); }}
              />
            }
            label={intl.getI18nMessage('label.application.key.default')}
          />
          <FormHelperText classes={{ root: classes.checkboxHelpText }}>
            {intl.getI18nMessage('label.application.key.default.help')}
          </FormHelperText>
          {(defaultKey !== keyIsDefault) &&
            <FormHelperText classes={{ root: classes.defaultKeyWarningText }}>
              {getI18nFormattedMessage('label.application.details.apikeys.devs.edit.message')}
            </FormHelperText>
          }
          {renderKeyStatus()}
          <Typography classes={{ root: classes.sectionTitle }} gutterBottom>
            {getI18nFormattedMessage('label.application.details.keys.authentication')}
          </Typography>
          <FormSelect
            id={'auth-provider'}
            name={intl.getI18nMessage('label.application.details.keys.auth.provider')}
            value={selectedAuthProviderUuid}
            data={authProviders}
            disabled={!!apiKey}
            handleChange={(value) => handleChange('authProvider', value)}
            noNativeSelect
            noNoneOption
          />

          <div className={classes.authMethodSelectContainer}>
            <FormSelect
              data={authMethods}
              disabled={!isDefaultPortalAuthProviderSelected || apiKey}
              handleChange={(value) => handleChange('authMethod', value)}
              fieldContainerClass={classes.selectField}
              id={'auth-method'}
              name={intl.getI18nMessage('label.application.details.keys.authentication.type')}
              noNativeSelect
              noNoneOption
              value={keyAuthMethod}
            />
            {isSecretAuthMethod && displayHashingOptions &&
              <Fragment>
                <FormSelect
                  fieldContainerClass={clsx(classes.secretType, classes.secretType)}
                  id={'secret-type'}
                  name={intl.getI18nMessage('label.application.details.keys.authentication.secret')}
                  value={keyIsHashed}
                  data={secretOptions}
                  handleChange={(value) => handleChange('isHashed', value)}
                  noNativeSelect
                  noNoneOption
                  disabled={!!apiKey}
                />
              </Fragment>
            }
          </div>
        </div>
        {(get(selectedAuthProvider, 'clientRegistrationType') === AUTH_PROVIDER_TYPE_PORTAL_DCR || clientRegistrationType === AUTH_PROVIDER_TYPE_PORTAL_DCR) &&
          <div className={classes.dcrSection}>
            <div className={classes.clientDefSelection}>
              <div className={classes.halfDiv}>
                {(get(selectedAuthProvider, 'clientRegistrationType') === AUTH_PROVIDER_TYPE_PORTAL_DCR || status === API_KEY_STATUS_PENDING_REGISTRATION) &&
                  <FormSelect
                    fieldContainerClass={classes.clientDefDropdown}
                    id="client-definition"
                    data-apim-test="client-definition"
                    name={intl.getI18nMessage('label.application.details.keys.auth.provider.client.definition')}
                    value={selectedClientDefinitionUuid}
                    data={clientDefinitions}
                    handleChange={(value) => onClientDefintionSelect(value)}
                    noNativeSelect
                    defaultText={clientDefinitions && clientDefinitions.length > 0 ?
                      intl.getI18nMessage('label.application.details.keys.auth.provider.client.definition.default') :
                      intl.getI18nMessage('label.application.details.keys.auth.provider.client.definition.no.options')}
                  />
                }
              </div>
              <div className={classes.clientDefButtons}>
                {(get(selectedAuthProvider, 'clientRegistrationType') === AUTH_PROVIDER_TYPE_PORTAL_DCR || status === API_KEY_STATUS_PENDING_REGISTRATION) &&
                  <Button
                    id="btn-relaod-definition"
                    data-apim-test="btn-relaod-definition"
                    className={classes.saveButton}
                    color="primary"
                    variant="outlined"
                    onClick={onReloadClientMetadata}
                    disabled={selectedClientDefinitionUuid === '0' ||
                      getMetadataToCompare(
                        selectedClientDefinition?.clientMetadata) === clientMetadata
                      }
                  >
                    {getI18nFormattedMessage('label.application.details.keys.auth.provider.client.definition.reload')}
                  </Button>
                }
                <Button
                  id="btn-expand"
                  data-apim-test="btn-expand"
                  className={classes.expandButton}
                  color="primary"
                  variant="outlined"
                  onClick={expandCollapseTextAreas}
                >
                  {getI18nFormattedMessage(
                    `label.application.details.keys.auth.provider.client.definition.${textAreaHeight === MIN_TEXT_AREA_HEIGHT ? 'expand' : 'collapse'}`,
                  )}
                </Button>
              </div>
            </div>
            <div className={classes.dcrTextAreas}>
              <FormTextField
                fieldContainerClass={get(selectedAuthProvider, 'clientRegistrationType') === AUTH_PROVIDER_TYPE_PORTAL_DCR || status === API_KEY_STATUS_PENDING_REGISTRATION ? classes.halfDiv: classes.fullDiv}
                id="client-definition-textarea"
                data-apim-text="client-definition-textarea"
                name={intl.getI18nMessage('label.application.details.keys.oauth.client.defintion.textarea')}
                value={clientMetadata}
                handleChange={value => { handleChange('clientMetadata', value || ''); }}
                handleBlur={value => { handleChange('clientMetadata', value || ''); }}
                helperText={intl.getI18nMessage('label.application.details.keys.oauth.client.defintion.textarea.helpertext')}
                multiline
                rows={textAreaHeight}
                maxLength="5000"
                error={invalidJsonMetadata}
                errorHelperText={invalidJsonMetadata}
              />
              {(get(selectedAuthProvider, 'clientRegistrationType') === AUTH_PROVIDER_TYPE_PORTAL_DCR || status === API_KEY_STATUS_PENDING_REGISTRATION) &&
                  <FormTextField
                  fieldContainerClass={clsx(classes.cdDesc, classes.halfDiv)}
                  id="client-definition-desc-textarea"
                  data-apim-text="client-definition-desc-textarea"
                  name={intl.getI18nMessage('label.application.details.keys.oauth.client.defintion.desc.textarea')}
                  value={selectedClientDefinition.description}
                  multiline
                  rows={textAreaHeight}
                  disabled
                />
                }
            </div>
          </div>
        }
        {isSecretAuthMethod &&
          <div>
            <Typography variant="h4" classes={{ root: classes.sectionTitle }} gutterBottom>
              {getI18nFormattedMessage('label.application.details.keys.oauth.section.title')}
            </Typography>
            <FormTextField
              variant="h4"
              id={`oauth-callback-url-key-${idx}`}
              name={intl.getI18nMessage('label.application.key.oauth.callback.url')}
              value={keyOAuthCallbackUrl}
              helperText={intl.getI18nMessage('label.application.key.oauth.callback.url.help')}
              multiline
              rows={1}
              optional
              maxLength="2048"
              handleChange={(value) => { handleChange('oauthCallbackUrl', value); }}
              disabled={!editable}
            />
            <FormTextField
              variant="h4"
              id={`oauth-scope-key`}
              name={intl.getI18nMessage('label.application.key.oauth.scope')}
              value={keyOAuthScope}
              helperText={intl.getI18nMessage('label.application.key.oauth.scope.help')}
              multiline
              rows={1}
              optional
              maxLength="4000"
              handleChange={(value) => { handleChange('scope', value); }}
              disabled={!editable}
            />
            <FormButtonGroup
              variant="h4"
              id={`oauth-type-key-`}
              name={'Type'}
              value={keyOAuthType}
              data={[
                { uuid: 'PUBLIC', name: getI18nFormattedMessage('label.application.details.apikeys.oauthtype.public') },
                { uuid: 'CONFIDENTIAL', name: getI18nFormattedMessage('label.application.details.apikeys.oauthtype.confidential') },
              ]}
              handleChange={(value) => { handleChange('type', value || null); }}
              disabled={!editable}
            />
          </div>
        }
        {get(selectedAuthProvider, 'clientRegistrationType') === AUTH_PROVIDER_TYPE_EXTERNAL && !apiKey &&
          <Fragment>
            <Typography variant="h4" classes={{ root: classes.sectionTitle }} gutterBottom>
              {getI18nFormattedMessage('label.application.details.keys.auth.provider.details')}
            </Typography>
            <FormTextField
              id="api-key"
              name={intl.getI18nMessage('label.application.details.keys.auth.provider.api.key')}
              value={userProvidedAPIKey}
              helperText={''}
              handleChange={(value) => { handleChange('userProvidedAPIKey', value); }}
              error={keyErrors.userProvidedAPIKey}
              errorHelperText={keyErrors.userProvidedAPIKey}
              maxLength="255"
            />
          </Fragment>
        }
      </div>
      <div className={classes.footer}>
        <Button
          id="btn-key-save"
          className={classes.saveButton}
          color="secondary"
          variant="contained"
          onClick={saveKey}
          disabled={!editable || isDisableSave || invalidJsonMetadata}
        >
          {getI18nFormattedMessage(`label.application.details.keys.key.${isLocking ? 'submit' : 'save'}.button`)}
        </Button>
        <Button
          id="btn-close"
          color="primary"
          variant="outlined"
          onClick={() => closeDialog()}
          className={classes.closeButton}
        >
          {getI18nFormattedMessage('label.application.details.keys.key.close.button')}
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  createKeyStatus: getCreateKeyStatus(state),
  updateKeyStatus: getUpdateKeyStatus(state),
  deleteKeyStatus: getDeleteKeyStatus(state),
  isUniqueKey: getIsUniqueKey(state),
  createdKey: getCreatedKey(state),
  deleteKeyErrors: getDeleteKeyErrors(state),
  clientDefinitions: getClientDefintionsList(state),
  oAuthClient: getSingleOAuthClient(state),
  createOAuthClientStatus: getCreateOAuthClientStatus(state),
  updateOAuthClientStatus: getUpdateOAuthClientStatus(state),
  deleteOAuthClientStatus: getDeleteOAuthClientStatus(state),
  oAuthClientErrors: getOAuthClientErrors(state),
  createdOAuthClient: getCreatedOAuthClient(state),
});

const mapDispatchToProps = {
  createKey,
  updateKey,
  deleteKey,
  fetchKeyNameUnique,
  fetchSingleApp,
  fetchKeyStoresByApiKey,
  fetchListDetails, // Refresh requests on Deleting a Key.
  fetchClientDefinitions,
  fetchOneOAuthClient,
  createOAuthClient,
  updateOAuthClient,
  deleteOAuthClient,
  resetOneOAuthClient,
};

EditKey.propTypes = {
  idx: number,
  applicationDetails: object,
  applicationUuid: string,
  authProviders: arrayOf(object),
  editable: bool,
  details: object,
  isUniqueKey: oneOfType([string, bool]),
  notifyMessages: func,
  appSecretHashingMetadata: object,
  closeAction: func,
  classes: object,
  updateKey: func,
  createKey: func,
  deleteKey: func,
  fetchKeyNameUnique: func,
  createKeyStatus: string,
  deleteKeyStatus: string,
  updateKeyStatus: string,
  createdKey: string,
  keyCount: number,
  isLocking: bool,
  onSaveDone: func,
  deleteKeyErrors: arrayOf(object),
  fetchSingleApp: func,
  isAdmin: bool,
  fetchKeyStoresByApiKey: func,
  isPortalAdmin: bool,
  certificateCount: number,
  user: object,
  fetchListDetails: func,
  clientDefinitions: arrayOf(object),
  oAuthClient: object,
  createOAuthClientStatus: string,
  updateOAuthClientStatus: string,
  deleteOAuthClientStatus: string,
  oAuthClientErrors: arrayOf(object),
  fetchOneOAuthClient: func,
  fetchClientDefinitions: func,
  deleteOAuthClient: func,
  updateOAuthClient: func,
  createOAuthClient: func,
  createdOAuthClient: object,
  resetOneOAuthClient: func,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(EditKey);
